import store from 'Store/store';
import { actions as industriesActions } from 'Slice/IndustriesSlice';
import { actions as serviceLinesActions } from 'Slice/ServiceLinesSlice';
import { actions as peopleActions } from 'Slice/PeopleSlice';
import { actions as groupActions } from 'Slice/GroupsSlice';
import { actions as clusterActions } from 'Slice/ClustersSlice';

type RouteModuleLoader = () => Promise<unknown>;

const routeModuleLoaders: Array<{ match: (path: string) => boolean; load: RouteModuleLoader }> = [
  { match: (p) => p.startsWith('/settings'), load: () => import('Pages/person/Settings') },
  { match: (p) => p.startsWith('/favorites'), load: () => import('Pages/person/Favorites') },
  { match: (p) => p.startsWith('/search/globalsearch'), load: () => import('Pages/search/SearchDetails') },
  { match: (p) => p.startsWith('/search/advanced'), load: () => import('Pages/search/AdvanceSearchResults') },
  { match: (p) => p.startsWith('/search'), load: () => import('Pages/search/GlobalSearchRedirections') },
  { match: (p) => p.startsWith('/onboarding'), load: () => import('Pages/onboardingRevamp/Onboarding') },
  { match: (p) => p.startsWith('/comparecompany'), load: () => import('Pages/companyComparison/CompanyComparison') },
  { match: (p) => p.startsWith('/similarpeople'), load: () => import('Pages/person/SimilarPeople') },
  { match: (p) => p.startsWith('/dailyiq'), load: () => import('Pages/dailyIq/DailyIQCompanies') },
  { match: (p) => p.startsWith('/article'), load: () => import('Pages/dailyIq/DailyIq') },
  { match: (p) => p.startsWith('/news'), load: () => import('Pages/customWidget/CustomWidget') },
  { match: (p) => p.startsWith('/arctypes'), load: () => import('Pages/arctypes/ArcTypes') },
  { match: (p) => p.startsWith('/salesforce'), load: () => import('Components/Salesforce/SalesforceRouter') },
  { match: (p) => p.startsWith('/password'), load: () => import('Pages/auth/Login') },
  { match: (p) => p.startsWith('/sso'), load: () => import('Pages/auth/SSO') },
  { match: (p) => p.startsWith('/webapp'), load: () => import('deeplinking/DeepLinkView') },
  { match: (p) => p.startsWith('/guestSearch'), load: () => import('Pages/search/GuestSearchResults') },
];

const prefetchedModules = new Set<string>();

export function prefetchRouteModule(path: string) {
  const loader = routeModuleLoaders.find(({ match }) => match(path));
  if (!loader || prefetchedModules.has(path)) return;
  prefetchedModules.add(path);
  loader.load();
}

export function prefetchGilroyModule() {
  import('gilroy-global');
}

export function prefetchNavRouteModules() {
  prefetchGilroyModule();
  [
    '/settings',
    '/favorites',
    '/search/globalsearch',
    '/search/advanced',
    '/comparecompany',
    '/similarpeople',
  ].forEach(prefetchRouteModule);
}

export function prefetchRouteData(path: string) {
  const state = store.getState() as Record<string, any>;

  if (path.startsWith('/industries')) {
    const industry = state.industry;
    if (industry && !industry.loading
      && !industry.followedIndustries?.length
      && !industry.recommendedIndustries?.length) {
      store.dispatch(industriesActions.getIndustries());
    }
    return;
  }

  if (path.startsWith('/servicelines')) {
    const sl = state.serviceline;
    if (sl && !sl.loading
      && !sl.followedServiceLines?.length
      && !sl.recommendedServiceLines?.length) {
      store.dispatch(serviceLinesActions.getServiceLines());
    }
    return;
  }

  if (path === '/people' || path.startsWith('/people?')) {
    const people = state.people;
    if (people && !people.followedPeopleLoading
      && !people.followedPeople?.length
      && !people.peopleCount) {
      store.dispatch(peopleActions.getFollowedPeople({ offset: 0 }));
    }
    const groups = state.groups;
    if (groups && !groups.allGroupsLoading && !groups.allGroupsLoaded && !groups.allGroups?.length) {
      store.dispatch(groupActions.getAllGroups({ offset: 0 }));
    }
  }

  if (path === '/companies' || path.startsWith('/companies?')) {
    const clusters = state.clusters;
    if (clusters && !clusters.activeClustersLoading && !clusters.activeClustersLoaded) {
      store.dispatch(clusterActions.getAccountClusters({ page: 1, type: 'active' }));
    }
  }
}

export function prefetchRoute(path: string) {
  prefetchRouteModule(path);
  prefetchRouteData(path);
}

/** Start the cluster details API call early (page is eagerly loaded in App). */
export function prefetchClusterDetailRoute(clusterId?: number) {
  if (!clusterId) return;
  store.dispatch(clusterActions.getClusterDetails({ clusterId }));
}
